// src/components/dashboards/CreateEnvironmentModal.tsx
// ---------------------------------------------------------------------------
// Modal to create a new environment and invite collaborators.
// Styled to match the onboarding language-selection tooltip: full dark
// gradient background, no light middle section, white-on-dark text.
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import UpgradeIcon from "@mui/icons-material/RocketLaunch";
import { useNavigate } from "react-router-dom";
import {
  athenaDarkPaperSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkPrimaryButtonSx,
  athenaHeaderIconBoxSx,
  athenaColors,
} from "../layout/AthenaDialogStyles";
import { useTranslation } from "../../i18n";
import api from "../../api/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type InviteRole = "admin" | "editor" | "contributor" | "viewer";

interface PendingInvite {
  email: string;
  role: InviteRole;
}

interface CreatedEnvironment {
  id: string;
  name: string;
}

interface CreateEnvironmentModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after environment is created (and invitations sent). Returns the new env. */
  onCreated: (env: CreatedEnvironment) => void;
}

interface UserOption {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
}

// ---------------------------------------------------------------------------
// Role config
// ---------------------------------------------------------------------------

const ROLES: { value: InviteRole; label: string; color: string }[] = [
  { value: "admin", label: "Admin", color: athenaColors.periwinkle },
  { value: "editor", label: "Editor", color: "#5b8def" },
  { value: "contributor", label: "Contributor", color: "#64b5f6" },
  { value: "viewer", label: "Viewer", color: "#90a4ae" },
];

// (Dark style tokens are now imported from AthenaDialogStyles)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CreateEnvironmentModal: React.FC<CreateEnvironmentModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // --- State ---
  const [envName, setEnvName] = useState("");
  const [inviteRole, setInviteRole] = useState<InviteRole>("viewer");
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpgradeError, setIsUpgradeError] = useState(false);

  // Autocomplete state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserOption[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- User search with debounce ---
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await api.get(`/auth/users/search/?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(resp.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  // --- Handlers ---

  const reset = () => {
    setEnvName("");
    setInviteRole("viewer");
    setPendingInvites([]);
    setCreating(false);
    setError(null);
    setIsUpgradeError(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAddInvite = (user: UserOption | null) => {
    if (!user) return;
    const email = user.email.toLowerCase();
    if (pendingInvites.some((i) => i.email === email)) {
      setError(t("env_modal.duplicate_email", "This email has already been added."));
      return;
    }
    setPendingInvites((prev) => [...prev, { email, role: inviteRole }]);
    setSearchQuery("");
    setSearchResults([]);
    setError(null);
  };

  const handleRemoveInvite = (email: string) => {
    setPendingInvites((prev) => prev.filter((i) => i.email !== email));
  };

  const handleCreate = useCallback(async () => {
    if (!envName.trim()) {
      setError(t("env_modal.name_required", "Environment name is required."));
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await api.post("/environments/", {
        name: envName.trim(),
      });
      const newEnv: CreatedEnvironment = response.data;

      const inviteResults = await Promise.allSettled(
        pendingInvites.map((invite) =>
          api.post(`/environments/${newEnv.id}/invite/`, {
            email: invite.email,
            role: invite.role,
          })
        )
      );

      const failures = inviteResults.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        console.warn(`${failures}/${pendingInvites.length} invitations failed`);
      }

      onCreated(newEnv);
      reset();
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.name?.[0] ||
        (data && typeof data === "object"
          ? Object.values(data).flat().join("; ")
          : null) ||
        t("env_modal.create_failed", "Failed to create environment.");
      setError(String(msg));

      // Detect plan/upgrade-related 403 errors so we can show an
      // "Upgrade Plan" call-to-action instead of a generic error.
      const detail = String(data?.detail || data?.error || "").toLowerCase();
      setIsUpgradeError(
        status === 403 &&
          (detail.includes("upgrade") ||
            detail.includes("plan allows") ||
            detail.includes("subscription"))
      );
    } finally {
      setCreating(false);
    }
  }, [envName, pendingInvites, onCreated, t]);

  // --- Render ---

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: alpha(theme.palette.common.black, 0.6) } } }}
    >
      {/* Header */}
      <DialogTitle sx={athenaDarkTitleSx}>
        <Box sx={athenaHeaderIconBoxSx}>
          <BusinessIcon sx={{ color: athenaColors.periwinkle }} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.common.white }}>
          {t("env_modal.title", "New Environment")}
        </Typography>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={athenaDarkContentSx}>
        {/* Environment Name */}
        <Typography
          variant="overline"
          sx={{
            color: athenaColors.periwinkle,
            letterSpacing: 1.2,
            fontWeight: 700,
            mb: 0.5,
            display: "block",
          }}
        >
          {t("env_modal.name_label", "Environment Name")}
        </Typography>
        <TextField
          fullWidth
          autoFocus
          placeholder={t("env_modal.name_placeholder", "e.g. Oncology Department")}
          value={envName}
          onChange={(e) => {
            setEnvName(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && pendingInvites.length === 0) {
              handleCreate();
            }
          }}
          size="small"
          sx={{ mb: 3 }}
        />

        <Divider
          sx={{ borderColor: alpha(athenaColors.periwinkle, 0.2), mb: 2.5 }}
        />

        {/* Invite Collaborators Section */}
        <Typography
          variant="overline"
          sx={{
            color: athenaColors.periwinkle,
            letterSpacing: 1.2,
            fontWeight: 700,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <GroupAddIcon sx={{ fontSize: 16 }} />
          {t("env_modal.invite_title", "Invite Collaborators")}
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: alpha(athenaColors.periwinkle, 0.5),
              fontStyle: "italic",
              ml: 0.5,
            }}
          >
            ({t("env_modal.optional", "optional")})
          </Typography>
        </Typography>

        {/* Role chips — matching language selection chip style */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            mb: 1.5,
            justifyContent: "flex-start",
          }}
        >
          {ROLES.map((role) => {
            const isSelected = inviteRole === role.value;
            return (
              <Chip
                key={role.value}
                label={t(`env_modal.role_${role.value}`, role.label)}
                onClick={() => setInviteRole(role.value)}
                variant={isSelected ? "filled" : "outlined"}
                size="small"
                sx={{
                  fontWeight: isSelected ? 700 : 400,
                  fontSize: "0.78rem",
                  borderColor: isSelected
                    ? athenaColors.periwinkle
                    : alpha(athenaColors.periwinkle, 0.35),
                  backgroundColor: isSelected
                    ? alpha(athenaColors.periwinkle, 0.25)
                    : "transparent",
                  color: isSelected ? theme.palette.common.white : alpha(theme.palette.common.white, 0.75),
                  "&:hover": {
                    backgroundColor: alpha(athenaColors.periwinkle, 0.15),
                    borderColor: athenaColors.periwinkle,
                    color: theme.palette.common.white,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* User search autocomplete */}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Autocomplete
            fullWidth
            freeSolo={false}
            options={searchResults}
            loading={searching}
            inputValue={searchQuery}
            onInputChange={(_e, value, reason) => {
              if (reason !== "reset") setSearchQuery(value);
            }}
            onChange={(_e, value) => {
              handleAddInvite(value as UserOption | null);
            }}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.email}${
                    option.first_name || option.last_name
                      ? ` (${[option.first_name, option.last_name]
                          .filter(Boolean)
                          .join(" ")})`
                      : ""
                  }`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              searchQuery.length < 3
                ? t("env_modal.type_to_search", "Type at least 3 characters to search")
                : t("env_modal.no_users_found", "No users found")
            }
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start !important",
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: alpha(athenaColors.periwinkle, 0.08),
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.email}
                </Typography>
                {(option.first_name || option.last_name) && (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {[option.first_name, option.last_name].filter(Boolean).join(" ")}
                  </Typography>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={t(
                  "env_modal.search_placeholder",
                  "Search users by email..."
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <PersonSearchIcon
                      sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.4), ml: 0.5, mr: -0.5 }}
                    />
                  ),
                  endAdornment: (
                    <>
                      {searching ? (
                        <CircularProgress size={16} sx={{ color: athenaColors.periwinkle }} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Pending invites list */}
        {pendingInvites.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: alpha(athenaColors.periwinkle, 0.1),
              border: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
              mb: 1,
            }}
          >
            {pendingInvites.map((invite) => (
              <Box
                key={invite.email}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.5,
                  "&:not(:last-child)": {
                    borderBottom: `1px solid ${alpha(
                      athenaColors.periwinkle,
                      0.15
                    )}`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.common.white,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {invite.email}
                  </Typography>
                  <Chip
                    label={t(`env_modal.role_${invite.role}`, invite.role)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      backgroundColor: alpha(
                        ROLES.find((r) => r.value === invite.role)?.color ||
                          "#90a4ae",
                        0.2
                      ),
                      color: theme.palette.common.white,
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveInvite(invite.email)}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.5),
                    "&:hover": { color: theme.palette.error.main },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main, display: "block" }}
            >
              {error}
            </Typography>
            {isUpgradeError && (
              <Button
                size="small"
                startIcon={<UpgradeIcon />}
                onClick={() => {
                  handleClose();
                  navigate("/billing");
                }}
                sx={{
                  mt: 1,
                  color: athenaColors.periwinkle,
                  borderColor: alpha(athenaColors.periwinkle, 0.5),
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  "&:hover": {
                    borderColor: athenaColors.periwinkle,
                    backgroundColor: alpha(athenaColors.periwinkle, 0.1),
                  },
                }}
                variant="outlined"
              >
                {t("billing.upgrade", "Upgrade")}
              </Button>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={athenaDarkActionsSx}>
        <Button
          onClick={handleClose}
          disabled={creating}
          sx={athenaDarkSecondaryButtonSx}
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleCreate}
          disabled={creating || !envName.trim()}
          startIcon={
            creating ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <AddIcon />
            )
          }
          sx={athenaDarkPrimaryButtonSx}
        >
          {creating
            ? t("env_modal.creating", "Creating...")
            : pendingInvites.length > 0
              ? t("env_modal.create_and_invite", "Create & Invite")
              : t("env_modal.create", "Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEnvironmentModal;
